import { useState, useEffect } from 'react';
import { Button, Snackbar, Alert, IconButton } from '@mui/material';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import CloseIcon from '@mui/icons-material/Close';

/**
 * InstallPrompt Component
 * 
 * Shows a prompt to install the PWA when the browser supports it.
 * Only appears on supported browsers (Chrome, Edge, etc.)
 * Can be dismissed and won't show again.
 */
export function InstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [showPrompt, setShowPrompt] = useState(false);

    useEffect(() => {
        // Check if user has already dismissed the prompt
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        if (dismissed) return;

        const handler = (e: Event) => {
            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();
            // Save the event so it can be triggered later
            setDeferredPrompt(e);
            // Show our custom install prompt
            setShowPrompt(true);
        };

        window.addEventListener('beforeinstallprompt', handler);

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
        };
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;

        // Show the install prompt
        deferredPrompt.prompt();

        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;

        console.log(`PWA install outcome: ${outcome}`);

        // Clear the deferred prompt
        setDeferredPrompt(null);
        setShowPrompt(false);

        // Track installation (optional analytics)
        if (outcome === 'accepted') {
            console.log('User accepted the install prompt');
        } else {
            console.log('User dismissed the install prompt');
        }
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        // Remember that user dismissed the prompt
        localStorage.setItem('pwa-install-dismissed', 'true');
    };

    if (!showPrompt) {
        return null;
    }

    return (
        <Snackbar
            open={showPrompt}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{ bottom: { xs: 80, sm: 24 } }}
        >
            <Alert
                severity="info"
                icon={<InstallMobileIcon />}
                action={
                    <>
                        <Button
                            color="inherit"
                            size="small"
                            onClick={handleInstall}
                            sx={{ fontWeight: 600 }}
                        >
                            Εγκατάσταση
                        </Button>
                        <IconButton
                            size="small"
                            aria-label="Κλείσιμο"
                            color="inherit"
                            onClick={handleDismiss}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </>
                }
                sx={{ width: '100%', alignItems: 'center' }}
            >
                Εγκαταστήστε την εφαρμογή στην αρχική σας οθόνη!
            </Alert>
        </Snackbar>
    );
}

export default InstallPrompt;
